//src/components/FormSettings/FormDesignLayout/BuilderDragLayer.jsx

import React, { useEffect } from "react";
import { useDragLayer } from "react-dnd";
import { Box } from "@mui/material";
import FieldRenderer from "../../Fields/FieldRenderer";
import { useFieldMetadata } from "../../../contexts/FieldMetadataContext";

/**
 * BuilderDragLayer (Enterprise)
 * - Renders the actual field being dragged as a "ghost"
 * - Avoids browser-native HTML5 drag preview glitches (cropped snapshots)
 * - Stable across Chrome/Safari/Edge and complex z-index overlays
 */

const layerStyles = {
  position: "fixed",
  pointerEvents: "none",
  zIndex: 20000,
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
};

function getItemStyles(currentOffset) {
  if (!currentOffset) return { display: "none" };
  const { x, y } = currentOffset;

  // Slight offset so the ghost isn't directly under the cursor
  const transform = `translate(${x + 8}px, ${y + 8}px)`;

  return {
    transform,
    WebkitTransform: transform,
  };
}

export default function BuilderDragLayer() {
  const { fieldsMetadata } = useFieldMetadata();

  const { isDragging, item, currentOffset, itemType } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  // ✅ Hooks must be unconditional: run this effect on every render.
  useEffect(() => {
    const prev = document.body.style.cursor;

    if (isDragging && itemType === "field") {
      document.body.style.cursor = "grabbing";
    } else {
      document.body.style.cursor = prev || "";
    }

    return () => {
      document.body.style.cursor = prev || "";
    };
  }, [isDragging, itemType]);

  // Only show for our builder field drags
  if (!isDragging || itemType !== "field" || !item?.id) return null;

  const meta = fieldsMetadata?.[item.id];
  if (!meta) return null;

  const ghostType = meta.type || item.type;
  const ghostLabel = meta.label || "Field";
  const ghostRequired = !!meta.required;

  return (
    <div style={layerStyles}>
      <div style={getItemStyles(currentOffset)}>
        <Box
          sx={{
            opacity: 0.65,
            borderRadius: 1,
            backgroundColor: "white",
            boxShadow: "0 14px 40px rgba(0,0,0,0.22)",
            padding: 1,
            width: item?.sourceRect?.width
              ? `${Math.round(item.sourceRect.width)}px`
              : 360,
            maxWidth: "none",
            minWidth: "unset",
          }}
        >
          <FieldRenderer
            id={item.id}
            type={ghostType}
            label={ghostLabel}
            required={ghostRequired}
            index={item.index ?? 0}
            mode="final"
            viewOnly
            fieldMetadata={meta}
            showDropZones={false}
            isDragging={true}
          />
        </Box>
      </div>
    </div>
  );
}